import React, { useEffect } from 'react';
import PageLayout from '../components/templates/PageLayout';
import LinkButton from '../components/atoms/LinkButton';
import LazyImage from '../components/atoms/LazyImage';
import ResponsiveLazyImage from '../components/atoms/ResponsiveLazyImage';
import { partners } from '../data/partners';

const externalLinkClass =
  'text-blue-600 underline hover:no-underline focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-yellow font-semibold inline-flex items-center';

const Partners = () => {
  useEffect(() => {
    document.title = 'Partners | DataRook';
  }, []);

  return (
    <PageLayout>
      <div className="py-16 bg-white">
        <div className="dr-container">
          <h1 className="text-4xl font-black mb-8 text-center text-navy">Our Partners</h1>
          <p className="text-xl text-center mb-12 max-w-3xl mx-auto">
            We&apos;re proud to collaborate with these outstanding organizations to bring innovative
            STEM education to youth through sports. Together, we&apos;re making learning powerful
            skills feel like play!
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-16">
            {partners.map((partner) => (
              <div key={partner.id} className="bg-gray-50 rounded-lg p-6 hover:shadow-lg transition-shadow">
                <div className="flex items-center mb-4">
                  <a
                    href={partner.website}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="block focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-yellow rounded"
                  >
                    <LazyImage
                      src={partner.logo}
                      alt={partner.name}
                      className="h-20 w-auto object-contain"
                      loading="lazy"
                    />
                  </a>
                </div>
                <h3 className="text-xl font-bold mb-2">{partner.name}</h3>
                <p className="text-gray-600 mb-3">{partner.description}</p>
                <a
                  href={partner.website}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={externalLinkClass}
                >
                  Visit Website
                  <svg className="w-4 h-4 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
                    />
                  </svg>
                </a>
              </div>
            ))}
          </div>

          <div className="bg-gradient-to-r from-navy to-blue-800 text-white rounded-xl p-8 mb-16">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-3xl font-black mb-6 text-center">Support Our Summer Partnership</h2>
              <div className="grid md:grid-cols-2 gap-8 items-center">
                <div>
                  <h3 className="text-2xl font-bold mb-4 text-yellow">East Tennessee Freedom Schools</h3>
                  <p className="mb-4">
                    We partner with East Tennessee Freedom Schools to bring footyLab to their
                    literacy-based summer program. Your support helps us reach underserved youth and
                    show them that STEM careers are within their reach.
                  </p>
                  <p className="mb-6">
                    Every donation directly impacts a student&apos;s ability to experience the magic
                    of data science through soccer.
                  </p>
                  <LinkButton
                    to="https://secure.givelively.org/donate/east-tennessee-freedom-schools/freedom-schools-datarook-deliver-stem-with-soccer"
                    target="_blank"
                    className="text-lg py-3 px-6"
                  >
                    Donate Now
                  </LinkButton>
                </div>
                <div className="relative">
                  <LazyImage
                    src="/ETSF_logo.png"
                    alt="East Tennessee Freedom Schools"
                    className="w-full max-w-sm mx-auto bg-white p-6 rounded-lg"
                    loading="lazy"
                  />
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl p-8 mb-16 shadow-lg">
            <h2 className="text-3xl font-black mb-6 text-center text-navy">
              Our Sponsors Make It Possible
            </h2>
            <div className="grid md:grid-cols-2 gap-8 items-center">
              <div>
                <p className="text-lg mb-4">
                  We&apos;re grateful to our sponsors whose support helps us bring footyLab to more
                  students. Their logos proudly appear on our team shirts, showing the community that
                  comes together to make STEM education accessible and fun.
                </p>
                <p className="text-lg mb-4">
                  Every sponsor plays a vital role in empowering the next generation of data
                  scientists and problem solvers through the beautiful game.
                </p>
              </div>
              <div className="relative">
                <ResponsiveLazyImage
                  originalSrc="/back_of_shirt_sponsors.png"
                  alt="DataRook shirt showing sponsor logos"
                  className="w-full rounded-lg shadow-md"
                  loading="lazy"
                  width={1280}
                  height={720}
                />
                <p className="text-sm text-gray-600 text-center mt-2">
                  Our team proudly wears our sponsors&apos; support
                </p>
              </div>
            </div>
          </div>

          <div className="bg-yellow rounded-xl p-12 text-center">
            <h2 className="text-3xl font-black mb-4 text-navy">Become a Partner</h2>
            <p className="text-xl mb-8 max-w-3xl mx-auto text-navy">
              Are you a school, community organization, or business looking to bring innovative STEM
              education to your students? Let&apos;s work together to inspire the next generation of
              data scientists and problem solvers!
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <LinkButton to="/contact" variant="partner" className="text-xl py-4 px-8">
                Partner With Us
              </LinkButton>
              <div className="text-navy">
                <p className="font-semibold">Questions?</p>
                <a
                  href="mailto:info@datarook.com"
                  className="underline hover:no-underline focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-navy"
                >
                  info@datarook.com
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </PageLayout>
  );
};

export default Partners;
